import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { User, Briefcase } from "@phosphor-icons/react";

const INTERVIEW_STAGES = {
  new_candidate: { label: "New Candidate", color: "#6B7280" },
  cv_submitted: { label: "CV Submitted", color: "#EC4899" },
  client_reviewing: { label: "Client Review", color: "#F59E0B" },
  interview_requested: { label: "Interview Req.", color: "#F97316" },
  interview_booked: { label: "Interview Booked", color: "#14B8A6" },
  first_interview: { label: "1st Interview", color: "#06B6D4" },
  second_interview: { label: "2nd Interview", color: "#0EA5E9" },
  offer_made: { label: "Offer Made", color: "#10B981" },
  placed: { label: "Placed", color: "#0F172A" },
  rejected: { label: "Rejected", color: "#EF4444" },
};
const STAGE_KEYS = Object.keys(INTERVIEW_STAGES);

export default function InterviewPipelinePage() {
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const { data } = await api.get("/placements?limit=500");
      setPlacements(data.items || []);
    } catch { toast.error("Failed to load pipeline"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const newStage = result.destination.droppableId;
    const placementId = result.draggableId;
    if (newStage === result.source.droppableId) return;
    setPlacements(prev => prev.map(p => p.id === placementId ? { ...p, interview_stage: newStage } : p));
    try {
      await api.put(`/placements/${placementId}/interview-stage`, { stage: newStage });
      toast.success(`Moved to ${INTERVIEW_STAGES[newStage]?.label}`);
    } catch {
      toast.error("Failed to update");
      loadData();
    }
  };

  const grouped = {};
  STAGE_KEYS.forEach(k => { grouped[k] = []; });
  placements.forEach(p => {
    const st = p.interview_stage || "new_candidate";
    if (grouped[st]) grouped[st].push(p);
    else grouped.new_candidate.push(p);
  });

  if (loading) return <div className="flex gap-3 overflow-x-auto p-4">{[1,2,3].map(i => <div key={i} className="w-56 h-80 skeleton-shimmer rounded-lg flex-shrink-0" />)}</div>;

  return (
    <div data-testid="interview-pipeline-page" className="h-full flex flex-col -m-4 md:-m-6">
      <div className="px-4 md:px-6 py-4 border-b border-[#E5E7EB] bg-white flex-shrink-0">
        <h1 className="text-xl font-bold text-[#0F172A]">Interview Pipeline</h1>
        <p className="text-xs text-[#6B7280] mt-0.5">Track candidates through the interview process</p>
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex gap-3 p-4 min-w-max h-full">
            {STAGE_KEYS.map(stageKey => {
              const meta = INTERVIEW_STAGES[stageKey];
              const items = grouped[stageKey] || [];
              return (
                <Droppable key={stageKey} droppableId={stageKey}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}
                      className={`w-52 flex-shrink-0 flex flex-col rounded-lg border transition-colors ${snapshot.isDraggingOver ? "bg-blue-50 border-blue-300" : "bg-[#F9FAFB] border-[#E5E7EB]"}`}>
                      <div className="px-3 py-2.5 border-b border-[#E5E7EB] flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color }} />
                          <span className="text-[11px] font-semibold text-[#0F172A] truncate">{meta.label}</span>
                          <span className="ml-auto text-[10px] font-medium text-[#6B7280] bg-white px-1.5 py-0.5 rounded">{items.length}</span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[120px]">
                        {items.map((p, idx) => (
                          <Draggable key={p.id} draggableId={p.id} index={idx}>
                            {(dragProv, snap) => (
                              <div ref={dragProv.innerRef} {...dragProv.draggableProps} {...dragProv.dragHandleProps}
                                className={`bg-white rounded-lg border p-2.5 cursor-grab active:cursor-grabbing ${snap.isDragging ? "shadow-lg border-blue-300" : "border-[#E5E7EB] hover:shadow-sm"}`}>
                                <p className="text-xs font-semibold text-[#0F172A] truncate flex items-center gap-1">
                                  <User size={12} className="text-[#C5A059]" /> {p.candidate_name || "Unknown"}
                                </p>
                                <p className="text-[10px] text-[#6B7280] truncate mt-0.5 flex items-center gap-1">
                                  <Briefcase size={10} /> {p.vacancy_title || "—"}
                                </p>
                                {p.company_name && <p className="text-[10px] text-[#1E3A8A] truncate">{p.company_name}</p>}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </div>
      </DragDropContext>
    </div>
  );
}
